import {
  CustomRequestOption,
  IconButton,
  Upload,
  showNotification,
  openDeleteDialog,
} from 'platform/components';
import {Hide, HStack, Icon, Link, Show, Space, Text, VStack} from 'platform/foundation';

import {Control, FieldValues, Path, useController} from 'react-hook-form';

import {isNotNil} from 'ramda-adjunct';

import {useUploadFileMutation} from '@dms/api/base';
import i18n from '@dms/i18n';
import {
  downloadHostedFile,
  getFileIcon,
  handleApiError,
  NoPermissionTooltip,
  printPDF,
  usePermissions,
} from '@dms/shared';

import {RequiredTestIdProps, openFile} from 'shared/web';

export interface FileFieldValue {
  fileId: string;
  fileName: string;
  fileUri: string;
  fileType: string;
}

export interface FileFieldProps<T extends FieldValues> extends RequiredTestIdProps {
  name: Path<T>;
  control: Control<T>;
  /** File type restrictions (e.g., 'application/pdf', 'image/*') */
  accept?: string;
  label?: string;
  isDisabled?: boolean;
}

/**
 * Generic file upload field integrated with react-hook-form
 * Uploads file to storage and stores metadata in form state
 * Document-context creation happens in form submit handler via useVehicleDocumentSync
 *
 * @example
 * <FileField
 *   name="batteryCertificateDocument"
 *   control={control}
 *   label="Battery Certificate"
 *   accept="application/pdf"
 *   data-testid="battery-certificate-upload"
 * />
 */
export function FileField<T extends FieldValues>(props: FileFieldProps<T>) {
  const [uploadFile] = useUploadFileMutation();

  const {field} = useController({
    name: props.name,
    control: props.control,
  });

  const uploadedFile = field.value as FileFieldValue | null;

  const [canReadDocuments, canDownloadDocuments] = usePermissions({
    permissionKeys: ['readDocuments', 'downloadDocuments'],
  });

  const handleUpload = async ({file}: CustomRequestOption) => {
    try {
      // Upload file to storage
      const uploadResponse = await uploadFile({
        file: file as File,
      })
        .unwrap()
        .catch(handleApiError);

      if (!uploadResponse) {
        return;
      }

      // Store file metadata in form value
      field.onChange({
        fileId: uploadResponse.fileId,
        fileName: uploadResponse.file.name,
        fileUri: uploadResponse.fileUri,
        fileType: uploadResponse.file.type,
      });
    } catch {
      showNotification.error(i18n.t('entity.documentExtraction.validations.errorUploadingFile'));
    }
  };

  const handlePrint = () => {
    if (uploadedFile?.fileUri) {
      printPDF(uploadedFile.fileUri);
    }
  };

  const handleDownload = () => {
    if (uploadedFile?.fileUri && uploadedFile?.fileName) {
      downloadHostedFile(uploadedFile.fileUri, uploadedFile.fileName);
    }
  };

  const handleDelete = () => {
    openDeleteDialog({
      onConfirm: () => {
        field.onChange(null);
      },
    });
  };

  if (isNotNil(uploadedFile)) {
    return (
      <VStack data-testid={props['data-testid']} spacing={2}>
        <HStack spacing={2} align="center" justify="flex-start">
          <Icon value={getFileIcon(uploadedFile.fileType)} size={6} />
          <Show when={canReadDocuments}>
            <Link onClick={() => openFile(uploadedFile.fileUri)} size="small">
              {uploadedFile.fileName}
            </Link>
          </Show>
          <Hide when={canReadDocuments}>
            <Text size="small">{uploadedFile.fileName}</Text>
          </Hide>
          <Space horizontal={1} />
          <NoPermissionTooltip shouldShowTooltip={!canReadDocuments || !canDownloadDocuments}>
            <IconButton
              icon="action/print"
              onClick={handlePrint}
              size="small"
              isDisabled={!canReadDocuments || !canDownloadDocuments}
              data-testid={`${props['data-testid']}-print`}
            />
          </NoPermissionTooltip>
          <NoPermissionTooltip shouldShowTooltip={!canReadDocuments}>
            <IconButton
              icon="file/download"
              onClick={handleDownload}
              size="small"
              isDisabled={!canReadDocuments}
              data-testid={`${props['data-testid']}-download`}
            />
          </NoPermissionTooltip>
          <IconButton
            icon="action/delete"
            onClick={handleDelete}
            severity="danger"
            size="small"
            data-testid={`${props['data-testid']}-delete`}
          />
        </HStack>
      </VStack>
    );
  }

  // Show upload button
  return (
    <Upload
      type="button"
      isDisabled={props.isDisabled}
      buttonVariant="ghostLink"
      uploadIcon="file/upload"
      uploadText={props.label ?? i18n.t('general.actions.upload')}
      customRequest={handleUpload}
      accept={props.accept ?? 'application/pdf'}
      data-testid={`${props['data-testid']}-upload`}
    />
  );
}
